import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { I18nData, I18nRedirector } from '@app/core/i18n/services';
import { UserService } from '@app/core/user/services/user/user.service';
import * as i18nActions from '@app/core/i18n/store/actions';
import * as fromI18n from '@app/core/i18n/store/reducers';
import * as fromUser from '@app/core/user/store/reducers';
import { PreferencesCookieService } from '@app/core/cookies';
import { I18nLanguage, I18nCurrency, I18nCountry } from '@app/core/i18n/models/interfaces';
import { PlatformService } from '@app/shared/services/platform/platform.service';
import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { Logger } from '@app/core/logging';

@Component({
  selector: 'vc-i18n',
  templateUrl: './i18n.component.html',
  styleUrls: ['./i18n.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class I18nComponent implements OnInit, OnDestroy {
  /**
   * Selected country.
   */
  public country: I18nCountry;

  /**
   * Selected language.
   */
  public language: I18nLanguage;

  /**
   * Selected currency.
   */
  public currency: I18nCurrency;

  /**
   * Available countries.
   */
  public countries$: Observable<any>;

  /**
   * Available currencies.
   */
  public currencies$: Observable<any>;

  /**
   * Available languages.
   */
  public languages$: Observable<any>;

  /**
   * has wallet ?
   */
  public hasWallet = false;

  private ngUnsubscribe = new Subject<void>();

  constructor(
    private i18nData: I18nData,
    private redirector: I18nRedirector,
    private platform: PlatformService,
    private preferencesCookieService: PreferencesCookieService,
    private userService: UserService,
    private store: Store<any>,
    private logger: Logger
  ) {}

  ngOnInit() {
    this.initCountries();
    this.initLanguages();
    this.initCurrencies();
    this.store.pipe(select(fromUser.isAuthenticated), takeUntil(this.ngUnsubscribe)).subscribe(
      res => {
        this.checkWallet();
      },
      err => {
        console.log(err);
      }
    );
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  validate(): void {
    if (this.platform.isBrowser) {
      this.updateCookies();
    }

    this.store.dispatch({
      type: i18nActions.UPDATE_COUNTRY,
      payload: this.country
    });
    this.store.dispatch({
      type: i18nActions.UPDATE_LANGUAGE,
      payload: this.language
    });
    this.store.dispatch({
      type: i18nActions.UPDATE_CURRENCY,
      payload: this.currency
    });

    this.store.pipe(select(fromUser.isAuthenticated), take(1)).subscribe(
      res => {
        const locale = this.language.isoCode;
        const userI18n = {
          country: this.country.isoCode,
          language: locale === 'en-US' ? 'us' : locale.split('-')[0],
          currency: this.currency.isoCode
        };
        if (res) {
          this.userService.updateUserSettings(userI18n).subscribe(
            result => {
              this.reload();
            },
            () => {
              // Silently catch exception
            }
          );
        } else {
          this.reload();
        }
      },
      err => {
        console.log(err);
      }
    );
  }

  private reload() {
    this.redirector.redirect(this.language.isoCode);
  }

  selectCountry(selectedCountry: I18nCountry): void {
    this.country = selectedCountry;
  }

  selectLanguage(selectedLanguage: I18nLanguage): void {
    this.language = selectedLanguage;
  }

  selectCurrency(selectedCurrency: I18nCurrency): void {
    this.currency = selectedCurrency;
  }

  private updateCookies() {
    const locale = this.language.isoCode;

    this.preferencesCookieService.setCkCookie(
      this.country.idRegion,
      locale === 'en-US' ? 'us' : locale.split('-')[0],
      this.currency.isoCode
    );
    this.preferencesCookieService.setCcCookie(this.country.isoCode);
  }

  private initCountries() {
    this.countries$ = this.i18nData.getCountries();

    this.store
      .pipe(select(fromI18n.getUserCountry))
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (state: I18nCountry) => {
          this.country = state;
        },
        () => {
          /* Silently catch exception */
        }
      );
  }

  private initCurrencies() {
    this.currencies$ = this.i18nData.getCurrencies();

    this.store
      .pipe(select(fromI18n.getUserCurrency))
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (state: I18nCurrency) => {
          this.currency = state;
        },
        () => {
          /* Silently catch exception */
        }
      );
  }

  private initLanguages() {
    this.languages$ = this.i18nData.getLanguages();

    this.store
      .pipe(select(fromI18n.getUserLang))
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (state: I18nLanguage) => {
          this.language = state;
        },
        () => {
          /* Silently catch exception */
        }
      );
  }

  isoDifferentSymbol(symbol, isoCode) {
    return symbol !== isoCode;
  }

  trackByFn(index, item) {
    return index;
  }

  checkWallet() {
    this.store
      .pipe(select(fromUser.getWallet))
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        res => {
          if (res) {
            this.hasWallet = true;
          }
        },
        error => {
          this.logger.error('[I18N] checkWallet: ' + error.message);
        }
      );
  }
}
